<template>
  <div class="pp-card" :style="{ '--pp-color': companyColor }">
    <div class="pp-card__thumb">
      <template-thumb :template="thumbTemplate" />
    </div>

    <div class="pp-card__content">
      <div class="pp-card__title-row">
        <h3 class="pp-card__title">Seu perfil público</h3>
        <span v-if="isPublished" class="pp-card__status pp-card__status--on">No ar</span>
        <span v-else-if="hasEverPublished" class="pp-card__status">Fora do ar</span>
        <pro-badge v-if="isPro" size="md" />
      </div>

      <p class="pp-card__text">{{ description }}</p>

      <div v-if="isPublished && page" class="pp-card__link">
        <link-icon size="16" />
        <span>{{ shortUrl }}</span>
        <button type="button" class="pp-card__copy" @click="copyLink">
          <copy-icon size="14" /> {{ copied ? "Copiado" : "Copiar" }}
        </button>
      </div>

      <div class="pp-card__actions">
        <template v-if="!hasEverPublished">
          <button type="button" class="pp-btn pp-btn--primary" @click="create">
            Criar meu perfil público
          </button>
          <button type="button" class="pp-btn" @click="seeTemplates">
            <eye-icon size="18" /> Ver modelos
          </button>
        </template>
        <template v-else>
          <button type="button" class="pp-btn pp-btn--primary" @click="edit">
            Editar meu perfil público
          </button>
          <a
            v-if="isPublished"
            class="pp-btn"
            :href="page.url"
            target="_blank"
            @click="ppTrack('public_profile_live_link_opened', { source })"
            rel="noopener noreferrer"
          >
            <external-link-icon size="18" /> Abrir
          </a>
          <button type="button" class="pp-btn" @click="openShare">
            <share-2-icon size="18" /> Compartilhar
          </button>
        </template>
      </div>
    </div>

    <share-dialog
      v-if="page"
      :active.sync="shareOpen"
      :source="source"
      @pro-required="openPaywall('share')"
    />
    <paywall-dialog :active.sync="paywallOpen" :source="paywallSource" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  LinkIcon,
  CopyIcon,
  EyeIcon,
  ExternalLinkIcon,
  Share2Icon,
} from "vue-feather-icons";
import mixin from "@/components/public-profile/mixin";
import TemplateThumb from "@/components/public-profile/TemplateThumb.vue";
import ProBadge from "@/components/public-profile/ProBadge.vue";
import ShareDialog from "@/components/public-profile/ShareDialog.vue";
import PaywallDialog from "@/components/public-profile/PaywallDialog.vue";
import {
  SECTIONS,
  copyText,
  displayUrl,
  formatDate,
  templateById,
} from "@/components/public-profile/constants";

export default {
  name: "PublicProfileCard",
  components: {
    TemplateThumb,
    ProBadge,
    ShareDialog,
    PaywallDialog,
    LinkIcon,
    CopyIcon,
    EyeIcon,
    ExternalLinkIcon,
    Share2Icon,
  },
  mixins: [mixin],
  props: {
    // Onde o card aparece, para o analytics: profile_card (aba Perfil) ou vacancies (Vagas pra você).
    source: { type: String, default: "profile_card" },
  },
  data: () => ({
    copied: false,
    shareOpen: false,
    paywallOpen: false,
    paywallSource: "",
  }),
  computed: {
    ...mapGetters("publicProfile", [
      "page",
      "plan",
      "isPro",
      "isPublished",
      "hasEverPublished",
      "proSections",
    ]),
    thumbTemplate() {
      return (this.page && this.page.template) || "classic";
    },
    shortUrl() {
      return displayUrl(this.page && this.page.url);
    },
    description() {
      if (!this.hasEverPublished) {
        return "Transforme seu perfil num site com link próprio, no visual que você escolher. Mande para quem quiser, sem anexar arquivo, e baixe em PDF quando precisar.";
      }
      const parts = [];
      if (this.isPro) parts.push(this.planText);
      parts.push(this.templateText);
      if (!this.isPublished) {
        parts.push("O link está desativado. Para ligar de novo, use Compartilhar.");
      }
      return parts.filter(Boolean).join(" ");
    },
    planText() {
      const plan = this.plan || {};
      if (plan.type === "lifetime") return "PRO para sempre.";
      const end = formatDate(plan.current_period_end);
      if (plan.type === "monthly" && end) {
        return plan.cancel_at_period_end ? `PRO até ${end}.` : `PRO até ${end}, renova sozinho.`;
      }
      return "";
    },
    templateText() {
      const name = templateById(this.thumbTemplate).name;
      const extras = ((this.page && this.page.sections) || [])
        .filter((s) => s.visible && this.proSections.includes(s.id))
        .map((s) => (SECTIONS[s.id] ? SECTIONS[s.id].label.split(" ")[0].toLowerCase() : s.id));
      if (!extras.length) return `Modelo ${name}.`;
      const last = extras.pop();
      const list = extras.length ? `${extras.join(", ")} e ${last}` : last;
      return `Modelo ${name}, com ${list}.`;
    },
  },
  created() {
    // Fora do editor vale o que esta salvo: rascunho de demonstracao so sobrevive
    // na volta do pagamento, dentro do editor.
    this.$store
      .dispatch("publicProfile/fetch", { keepDraft: false, peek: true })
      .then(() => {
        let state = "not_created";
        if (this.isPublished) state = "published";
        else if (this.hasEverPublished) state = "unpublished";
        this.ppTrack("public_profile_card_viewed", { state, source: this.source }, { setPerson: true });
      })
      .catch(() => {
        // API fora do ar: o bloco fica no estado de "criar" e o editor trata o erro.
        this.ppTrack("public_profile_card_load_failed", { source: this.source });
      });
  },
  methods: {
    goToEditor(query = {}) {
      this.$router.push({
        name: "public-profile-editor",
        params: { company_label: this.companyLabel },
        query,
      });
    },
    create() {
      this.ppTrack("public_profile_create_clicked", { source: this.source });
      this.goToEditor({ modelos: "1" });
    },
    seeTemplates() {
      this.ppTrack("public_profile_templates_viewed", { source: this.source });
      this.goToEditor({ modelos: "1" });
    },
    edit() {
      this.ppTrack("public_profile_editor_clicked", { source: this.source, first_time: false });
      this.goToEditor();
    },
    openShare() {
      this.ppTrack("public_profile_share_opened", { source: this.source });
      this.shareOpen = true;
    },
    openPaywall(source) {
      this.shareOpen = false;
      this.paywallSource = source;
      this.paywallOpen = true;
    },
    async copyLink() {
      const ok = await copyText(this.page.url);
      if (!ok) {
        this.ppError("Não deu para copiar. Selecione o link e copie à mão.");
        return;
      }
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
      this.ppTrack("public_profile_link_copied", { source: this.source });
    },
  },
};
</script>

<style lang="scss">
.pp-card {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 24px;
  border: 0.5px solid #a8a8a8;
  border-radius: 16px;
  background: #fff;
  font-family: Roboto, sans-serif;

  &__thumb {
    flex-shrink: 0;
    width: 240px;
    overflow: hidden;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;
  }

  &__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  &__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  // "& &__" (.pp-card .pp-card__title): o card tambem aparece na tela de vagas, cujo CSS
  // nao escopado estiliza todo h3 e p da secao e ganharia de uma classe so.
  & &__title {
    color: #333;
    font-size: 20px;
    font-weight: 600;
  }

  &__status {
    padding: 2px 8px;
    border: 0.5px solid #a8a8a8;
    border-radius: 4px;
    background: #ecebeb;
    color: #4f4f4f;
    font-size: 12px;
    font-weight: 500;

    &--on {
      border-color: #86efac;
      background: #dcfce7;
      color: #166534;
    }
  }

  & &__text {
    margin-top: 6px;
    color: #4f4f4f;
    font-size: 16px;
    line-height: 1.4;
  }

  &__link {
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    margin-top: 12px;
    padding: 4px 4px 4px 12px;
    border-radius: 8px;
    background: #f2f4f7;
    color: #333;
    font-size: 14px;

    svg {
      flex-shrink: 0;
    }

    > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__copy {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 4px;
    height: 28px;
    padding: 0 8px;
    border: none;
    border-radius: 6px;
    background: #fff;
    color: #333;
    font-family: Roboto, sans-serif;
    font-size: 13px;
    cursor: pointer;

    &:hover {
      background: #e7edf0;
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    padding: 16px;

    &__thumb {
      width: 100%;
    }

    &__actions .pp-btn {
      flex: 1 1 100%;
    }
  }
}
</style>
